import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Compass, Cpu, Sun, Sparkles, Briefcase, Leaf, GraduationCap, Zap, Activity, HardHat, Wrench, Flame, Hammer } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { useBrand, useBrands, useEditorial } from "@/data/hooks";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { BrandMark } from "@/components/BrandMarks";
import Breadcrumbs from "@/components/Breadcrumbs";

const ICON_MAP = { Compass, Cpu, Sun, Sparkles, Briefcase, Leaf, GraduationCap, Zap, Activity };

export default function BrandPage() {
 const { slug } = useParams();
 const brand = useBrand(slug);
 const editorial = useEditorial(slug);
 const BRANDS = useBrands();
 const { t, locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 const { scrollY } = useScroll();
 const y = useTransform(scrollY, [0, 800], [0, 120]);

 if (!brand) return <Navigate to="/marques" replace />;

 const otherBrands = BRANDS.filter((b) => b.slug !== brand.slug).slice(0, 3);
 const isProductHero = brand.heroStyle === "product";

 return (
 <div data-testid={`page-brand-${brand.slug}`}>
 {/* HERO, image-cover OR product showcase */}
 {isProductHero ? (
 <section className="relative min-h-[88vh] overflow-hidden bg-[#F5EDE2]">
 <div className="relative z-10 mx-auto max-w-[1400px] h-full px-6 md:px-10 lg:px-14 pt-28 md:pt-36 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
 <div className="md:col-span-5">
 <Link to="/marques" data-testid="brand-back-link" className="label-overline text-nanda-inkSoft hover:text-nanda-ink mb-10 inline-flex items-center gap-2 transition-colors duration-300">
 <ArrowLeft size={14} strokeWidth={1.5} /> {t("nav.brands", "Marques")}
 </Link>
 <div className="label-overline text-nanda-inkSoft mb-5">{brand.category}</div>
 <motion.h1
 initial={{ opacity: 0, y: 30 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
 className="font-serif text-nanda-ink text-6xl md:text-7xl lg:text-[110px] leading-[0.95] tracking-tight"
 >
 {brand.name}
 </motion.h1>
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ duration: 1, delay: 0.3 }}
 className="mt-8 max-w-xl text-nanda-inkSoft font-serif text-2xl md:text-3xl leading-tight"
 >
 {editorial?.headline || brand.tagline}
 </motion.div>
 </div>
 <motion.div
 initial={{ opacity: 0, scale: 0.96 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ duration: 1.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
 className="md:col-span-7"
 >
 <div className="aspect-square w-full overflow-hidden">
 <img src={brand.image} alt={brand.name} className="h-full w-full object-contain" loading="eager" />
 </div>
 </motion.div>
 </div>
 </section>
 ) : (
 <section className="relative h-[92vh] min-h-[640px] overflow-hidden bg-nanda-ink">
 <motion.div style={{ y }} className="absolute inset-0">
 <img
 src={brand.image}
 alt={`${brand.name}, ${brand.short}`}
 className="h-full w-full object-cover"
 loading="eager"
 />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/30 via-nanda-ink/15 to-nanda-ink/60" />
 </motion.div>
 <div className="relative z-10 mx-auto max-w-[1400px] h-full px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-20 md:pb-28">
 <Link
 to="/marques"
 data-testid="brand-back-link"
 className="label-overline text-white/80 hover:text-white mb-10 inline-flex items-center gap-2 transition-colors duration-300"
 >
 <ArrowLeft size={14} strokeWidth={1.5} /> {t("nav.brands", "Marques")}
 </Link>
 <div className="flex items-center gap-4 mb-5">
 <BrandMark slug={brand.slug} size={44} className="text-white/90" />
 <div className="label-overline text-white/80">{brand.category}</div>
 </div>
 <Breadcrumbs items={[{ label: l("Marques", "Brands", "Marcas"), to: "/marques" }, { label: brand.name }]} className="mb-5 !text-white/70" />
 <motion.h1
 initial={{ opacity: 0, y: 30 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
 className="font-serif text-white text-5xl md:text-7xl lg:text-[110px] leading-[0.95] tracking-tight"
 >
 {brand.name}
 </motion.h1>
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ duration: 1, delay: 0.3 }}
 className="mt-8 max-w-3xl text-white/90 font-serif text-2xl md:text-3xl leading-tight"
 >
 {editorial?.headline || brand.tagline}
 </motion.div>
 {brand.slug === "academie" && (
 <motion.div
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.9, delay: 0.55 }}
 className="mt-12"
 >
 <a
 href="#poles"
 data-testid="academie-hero-cta"
 className="inline-flex items-center gap-3 bg-white text-nanda-ink px-7 py-4 hover:bg-nanda-sand transition-colors duration-500 label-overline"
 >
 {l("Découvrir nos programmes", "Discover our programmes", "Descobrir os nossos programas")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </a>
 </motion.div>
 )}
 </div>
 </section>
 )}

 {/* === ACADÉMIE. Minimalist dedicated layout === */}
 {brand.slug === "academie" ? (
 <>
 {/* PÔLES, 5 pillars in 3+2 centered layout */}
 <section id="poles" className="border-t border-nanda-line bg-nanda-bg" data-testid="academie-poles-section">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{l("Nos pôles", "Our pillars", "Os nossos pólos")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {l("Nos pôles de formation.", "Our training pillars.", "Os nossos pólos de formação.")}
 </h2>
 </FadeUp>
 <Stagger className="flex flex-wrap justify-center gap-6 md:gap-8">
 {editorial?.collections?.map((col, idx) => {
 const Icon = ICON_MAP[col.icon];
 return (
 <StaggerItem
 key={idx}
 className="group w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)] bg-white rounded-2xl border border-nanda-line/70 shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 p-8 md:p-10 flex flex-col"
 data-testid={`academie-pole-${idx}`}
 >
 <div className="flex items-start justify-between mb-8">
 {Icon && (
 <div className="text-nanda-terracotta group-hover:scale-110 transition-transform duration-500">
 <Icon size={34} strokeWidth={1.4} />
 </div>
 )}
 <div className="label-overline text-nanda-muted">0{idx + 1}</div>
 </div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-5">{col.title}</h3>
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed mb-8">{col.intro}</p>
 <Link
 to={`/contact?brand=academie&pole=${col.slug || ""}`}
 data-testid={`academie-pole-cta-${col.slug || idx}`}
 className="mt-auto inline-flex items-center gap-2 label-overline text-nanda-terracotta hover:text-nanda-ink transition-colors duration-300 group/link"
 aria-label={l(`En savoir plus sur ${col.title}`, `Learn more about ${col.title}`, `Saber mais sobre ${col.title}`)}
 >
 {l("En savoir plus", "Learn more", "Saber mais")}
 <ArrowUpRight size={14} strokeWidth={1.7} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-0.5" />
 </Link>
 </StaggerItem>
 );
 })}
 </Stagger>
 </div>
 </section>

 {/* VISION */}
 {editorial?.vision && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid="academie-vision-section">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-5">{l("Notre vision", "Our vision", "A nossa visão")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-12">
 {editorial.vision.title}
 </h2>
 <div className="rule-gold w-24 mb-10" />
 <div className="space-y-6 text-nanda-inkSoft text-lg md:text-xl leading-relaxed max-w-3xl">
 {editorial.vision.body.map((p, i) => <p key={i}>{p}</p>)}
 </div>
 </FadeUp>
 </div>
 </section>
 )}

 {/* IMPLANTATIONS. Zones d'intervention Guinée-Bissau */}
 <section className="border-t border-nanda-line" data-testid="academie-locations-section">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{l("Nos zones d'intervention", "Our areas of activity", "As nossas zonas de intervenção")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {l("Deux zones. Trois langues.", "Two areas. Three languages.", "Duas zonas. Três línguas.")}
 </h2>
 <p className="mt-8 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {l(
 "NANDA SÁ Académie intervient à Canchungo, en Guinée-Bissau, et à Ziguinchor, au Sénégal. Toutes nos formations sont proposées en français, portugais et anglais.",
 "NANDA SÁ Académie operates in Canchungo (Guinea-Bissau) and Ziguinchor (Senegal). All our training programmes are delivered in French, Portuguese and English.",
 "A NANDA SÁ Académie intervém em Canchungo (Guiné-Bissau) e em Ziguinchor (Senegal). Todas as nossas formações são propostas em francês, português e inglês."
 )}
 </p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 <StaggerItem className="bg-nanda-bg p-10 md:p-14" data-testid="academie-zone-canchungo">
 <div className="label-overline text-nanda-terracotta mb-4">01 · {l("Guinée-Bissau", "Guinea-Bissau", "Guiné-Bissau")}</div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink mb-4">Canchungo</h3>
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">
 {l(
 "Zone d'intervention et de développement de NANDA SÁ Académie, avec des activités de formation et d'accompagnement destinées aux jeunes, aux entrepreneurs et aux professionnels.",
 "Area of activity and development for NANDA SÁ Académie, with training and mentoring programmes for young people, entrepreneurs and professionals.",
 "Zona de intervenção e desenvolvimento da NANDA SÁ Académie, com actividades de formação e acompanhamento destinadas a jovens, empresários e profissionais."
 )}
 </p>
 </StaggerItem>
 <StaggerItem className="bg-nanda-bg p-10 md:p-14" data-testid="academie-zone-bafata">
 <div className="label-overline text-nanda-terracotta mb-4">02 · {l("Guinée-Bissau", "Guinea-Bissau", "Guinea-Bissau")}</div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink mb-4">Bafatá</h3>
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">
 {l(
 "Zone d'intervention et de développement de NANDA SÁ Académie, au Sénégal et en Guinée-Bissau, pour des formations et des projets ancrés dans le territoire ouest-africain.",
 "Area of activity and development for NANDA SÁ Académie, in Senegal and Guinea-Bissau, for training programmes and projects rooted in the West African territory.",
 "Zona de intervenção e desenvolvimento da NANDA SÁ Académie, no Senegal e na Guiné-Bissau, para formações e projectos enraizados no território oeste-africano."
 )}
 </p>
 </StaggerItem>
 </Stagger>
 <FadeUp delay={0.2} className="mt-14 flex flex-wrap gap-3">
 <span className="label-overline px-4 py-2 border border-nanda-line rounded-full text-nanda-ink" data-testid="lang-fr">{l("Français", "French", "Francês")}</span>
 <span className="label-overline px-4 py-2 border border-nanda-line rounded-full text-nanda-ink" data-testid="lang-pt">{l("Portugais", "Portuguese", "Português")}</span>
 <span className="label-overline px-4 py-2 border border-nanda-line rounded-full text-nanda-ink" data-testid="lang-en">{l("Anglais", "English", "Inglês")}</span>
 </FadeUp>
 </div>
 </section>

 {/* CTA. Construisons ensemble */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid="academie-cta-section">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-7">
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05] text-nanda-ink">
 {l("Construisons ensemble les talents de demain.", "Let us build tomorrow's talents together.", "Construamos juntos os talentos de amanhã.")}
 </h2>
 <p className="mt-6 max-w-xl text-nanda-inkSoft text-base md:text-[17px]">
 {l(
 "Vous souhaitez rejoindre une formation, devenir partenaire ou accompagner nos programmes ?",
 "Would you like to join a training programme, become a partner or support our programmes?",
 "Deseja integrar uma formação, tornar-se parceiro ou apoiar os nossos programas?"
 )}
 </p>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 flex md:justify-end items-end">
 <Link
 to="/contact?brand=academie"
 data-testid="academie-cta-contact"
 className="inline-flex items-center gap-3 bg-nanda-terracotta text-white px-7 py-4 hover:bg-[#8F5B3F] transition-colors duration-500 label-overline"
 >
 {l("Nous contacter", "Contact us", "Contactar-nos")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>

 {/* Other brands */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <div className="label-overline text-nanda-muted mb-10">{l("Les autres maisons", "The other houses", "As outras casas")}</div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {otherBrands.map((b) => (
 <Link
 key={b.slug}
 to={`/marques/${b.slug}`}
 data-testid={`other-brand-${b.slug}`}
 className={`group relative block overflow-hidden border border-nanda-line aspect-[4/5] ${b.heroStyle === "product" ? "bg-[#F5EDE2]" : ""}`}
 >
 <img
 src={b.image}
 alt={b.name}
 loading="lazy"
 className={`absolute inset-0 h-full w-full transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${b.heroStyle === "product" ? "object-contain p-6" : "object-cover"}`}
 />
 {b.heroStyle !== "product" && (
 <div className="absolute inset-0 bg-gradient-to-t from-nanda-ink/70 via-nanda-ink/10 to-transparent" />
 )}
 <div className="absolute inset-0 p-7 flex flex-col justify-end">
 <div className={`font-serif text-3xl ${b.heroStyle === "product" ? "text-nanda-ink" : "text-white"}`}>{b.name}</div>
 <div className={`label-overline mt-2 ${b.heroStyle === "product" ? "text-nanda-inkSoft" : "text-white/80"}`}>{b.category}</div>
 </div>
 </Link>
 ))}
 </div>
 </div>
 </section>
 </>
 ) : (
 <>
 {/* PULL QUOTE + Intro */}
 {editorial && (
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-36">
 <FadeUp>
 <blockquote className="font-serif text-3xl md:text-5xl leading-[1.15] text-balance text-nanda-ink">
 « {editorial.pullQuote} »
 </blockquote>
 </FadeUp>
 <FadeUp delay={0.1} className="mt-16 max-w-2xl space-y-6 text-nanda-inkSoft text-[17px] md:text-lg leading-relaxed">
 {editorial.introduction.map((p, i) => (
 <p key={i}>{p}</p>
 ))}
 </FadeUp>
 </div>
 </section>
 )}

 {/* STORY, image + text two-column */}
 {editorial?.story && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
 <FadeUp className="md:col-span-6">
 <div className={`aspect-[4/5] overflow-hidden ${isProductHero ? "bg-[#F5EDE2] flex items-center justify-center" : ""}`}>
 <img
 src={editorial.story.image}
 alt={editorial.story.title}
 loading="lazy"
 className={`h-full w-full ${isProductHero ? "object-contain p-8" : "object-cover"}`}
 />
 </div>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-6 md:pl-8">
 <div className="label-overline text-nanda-muted mb-5">{l("Notre histoire", "Our story", "A nossa história")}</div>
 <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-10">{editorial.story.title}</h2>
 <div className="space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {editorial.story.body.map((p, i) => <p key={i}>{p}</p>)}
 </div>
 </FadeUp>
 </div>
 </section>
 )}

 {/* VISION + MISSION */}
 {editorial && (editorial.vision || editorial.mission) && (
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
 {editorial.vision && (
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-5">{l("Vision", "Vision", "Visão")}</div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-8">{editorial.vision.title}</h3>
 <div className="space-y-5 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {editorial.vision.body.map((p, i) => <p key={i}>{p}</p>)}
 </div>
 </FadeUp>
 )}
 {editorial.mission && (
 <FadeUp delay={0.1}>
 <div className="label-overline text-nanda-muted mb-5">{l("Mission", "Mission", "Missão")}</div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-8">{editorial.mission.title}</h3>
 <p className="font-serif text-2xl md:text-3xl leading-snug text-nanda-ink mb-6">{editorial.mission.body}</p>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.mission.detail}</p>
 </FadeUp>
 )}
 </div>
 </section>
 )}

 {/* VALUES */}
 {editorial?.values && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16">
 <div className="label-overline text-nanda-muted mb-4">{l("Nos valeurs", "Our values", "Os nossos valores")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">{`${brand.name}, ${l("Nos valeurs", "Our values", "Os nossos valores")}`}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {editorial.values.map((v, i) => (
 <StaggerItem
 key={i}
 className="bg-nanda-bg p-10 md:p-14"
 data-testid={`brand-value-${brand.slug}-${i}`}
 >
 <div className="label-overline text-nanda-muted">0{i + 1}</div>
 <h3 className="font-serif text-3xl md:text-4xl mt-5">{v.title}</h3>
 <p className="mt-6 max-w-md text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{v.body}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* COLLECTIONS */}
 {editorial?.collections && (
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20">
 <div className="label-overline text-nanda-muted mb-4">
 {editorial.collections[0]?.icon ? l("Nos pôles", "Our pillars", "Os nossos pólos") : l("Nos collections", "Our collections", "As nossas colecções")}
 </div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">
 {editorial.collections[0]?.icon ? l("Six pôles de formation.", "Six training pillars.", "Seis pólos de formação.") : l("Les collections", "The collections", "As colecções")}
 </h2>
 </FadeUp>

 {editorial.collections[0]?.icon ? (
 // Icon-card grid layout, elegant, minimal, no photos
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {editorial.collections.map((col, idx) => {
 const Icon = ICON_MAP[col.icon];
 return (
 <StaggerItem key={idx} className="bg-nanda-bg p-8 md:p-10 lg:p-12 group hover:bg-nanda-bgAlt transition-colors duration-700" data-testid={`academie-pole-${idx}`}>
 <div className="flex items-start justify-between mb-8">
 {Icon && (
 <div className="text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500">
 <Icon size={32} strokeWidth={1.2} />
 </div>
 )}
 <div className="label-overline text-nanda-muted">0{idx + 1}</div>
 </div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-5">{col.title}</h3>
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{col.intro}</p>
 </StaggerItem>
 );
 })}
 </Stagger>
 ) : (
 <div className="space-y-px bg-nanda-line border border-nanda-line">
 {editorial.collections.map((col, idx) => (
 <FadeUp key={idx}>
 <div className="bg-nanda-bg grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 p-8 md:p-14 items-center">
 <div className={`md:col-span-5 aspect-[4/5] overflow-hidden ${idx % 2 === 1 ? "md:order-2" : ""} ${isProductHero ? "bg-[#F5EDE2] flex items-center justify-center" : ""}`}>
 {col.image ? (
 <img src={col.image} alt={col.title} loading="lazy" className={`h-full w-full ${isProductHero ? "object-contain p-6" : "object-cover"}`} />
 ) : (
 <div className="h-full w-full bg-nanda-bgAlt border border-nanda-line/50 flex items-center justify-center" data-testid="collection-placeholder">
 <span className="label-overline text-nanda-muted">{l("Visuel à venir", "Image coming soon", "Imagem em breve")}</span>
 </div>
 )}
 </div>
 <div className="md:col-span-7">
 <div className="label-overline text-nanda-muted">0{idx + 1}</div>
 <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl mt-5">{col.title}</h3>
 <p className="mt-6 text-nanda-inkSoft text-base md:text-[17px] max-w-xl leading-relaxed">{col.intro}</p>
 <ul className="mt-8 space-y-3">
 {col.items && col.items.map((it, j) => (
 <li key={j} className="flex items-center gap-3 text-nanda-ink text-[15px]">
 <span className="h-px w-6 bg-nanda-ink" />
 {it}
 </li>
 ))}
 </ul>
 </div>
 </div>
 </FadeUp>
 ))}
 </div>
 )}
 </div>
 </section>
 )}

 {/* ═══════════════════════════════════════════════════════════════════
 ÉTAPE 5. ENRICHISSEMENT ÉDITORIAL PAR MARQUE
 Ces sections apparaissent uniquement si le contenu enrichi existe.
 ═══════════════════════════════════════════════════════════════════ */}

 {/* RITUELS, NANDA SÁ Cosmétiques */}
 {editorial?.rituals && (
 <section className="border-t border-nanda-line" data-testid={`brand-rituals-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.rituals.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.rituals.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.rituals.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {editorial.rituals.items.map((r, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12" data-testid={`ritual-${i}`}>
 <div className="flex items-baseline justify-between mb-8">
 <span className="label-overline text-nanda-terracotta">{r.number}</span>
 <span className="label-overline text-nanda-muted">{r.duration}</span>
 </div>
 <div className="label-overline text-nanda-muted mb-3">{r.time}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-10">{r.title}</h3>
 <ol className="space-y-5 text-nanda-inkSoft text-[15px] leading-relaxed">
 {r.steps.map((s, j) => (
 <li key={j} className="flex gap-4">
 <span className="font-serif text-nanda-terracotta shrink-0 num-editorial">0{j + 1}</span>
 <span>{s}</span>
 </li>
 ))}
 </ol>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* INGRÉDIENTS EMBLÉMATIQUES, NANDA SÁ Cosmétiques */}
 {editorial?.ingredients && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid={`brand-ingredients-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.ingredients.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.ingredients.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.ingredients.intro}</p>
 </FadeUp>
 <Stagger className="divide-y divide-nanda-line border-y border-nanda-line">
 {editorial.ingredients.items.map((ing, i) => (
 <StaggerItem key={i} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-8 md:py-10 items-baseline group hover:bg-nanda-bg transition-colors duration-500 -mx-4 px-4" data-testid={`ingredient-${i}`}>
 <div className="md:col-span-1 label-overline text-nanda-terracotta num-editorial">0{i + 1}</div>
 <div className="md:col-span-3">
 <div className="font-serif text-3xl md:text-4xl text-nanda-ink leading-tight">{ing.name}</div>
 <div className="text-nanda-muted italic text-sm mt-2">{ing.latin}</div>
 </div>
 <div className="md:col-span-3 label-overline text-nanda-inkSoft">{ing.origin}</div>
 <div className="md:col-span-5 text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{ing.virtue}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* TERROIRS. NANDA SÁ */}
 {editorial?.terroirs && (
 <section className="border-t border-nanda-line" data-testid={`brand-terroirs-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.terroirs.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.terroirs.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.terroirs.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {editorial.terroirs.items.map((t, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-14" data-testid={`terroir-${i}`}>
 <div className="flex items-baseline justify-between mb-6">
 <div className="label-overline text-nanda-muted num-editorial">0{i + 1}</div>
 <div className="label-overline text-nanda-terracotta">{t.country}</div>
 </div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink mb-4">{t.region}</h3>
 <div className="rule-gold w-24 my-6" />
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed mb-6">{t.body}</p>
 <div className="label-overline text-nanda-ink">{t.signature}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* PRODUITS EMBLÉMATIQUES. NANDA SÁ */}
 {editorial?.emblematicProducts && (
 <section className="border-t border-nanda-line bg-nanda-sand/30" data-testid={`brand-emblematic-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.emblematicProducts.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.emblematicProducts.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.emblematicProducts.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {editorial.emblematicProducts.items.map((p, i) => (
 <StaggerItem key={p.slug} className="bg-nanda-bg flex flex-col overflow-hidden" data-testid={`emblematic-${p.slug}`}>
 {p.image && (
 <div className="aspect-[4/3] overflow-hidden bg-nanda-line">
 <img
 src={p.image}
 alt={typeof p.name === "string" ? p.name : ""}
 loading="lazy"
 className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-105"
 />
 </div>
 )}
 <div className="p-8 md:p-10 flex flex-col flex-1">
 <div className="flex items-baseline justify-between mb-5">
 <div className="label-overline text-nanda-terracotta num-editorial">{String(i + 1).padStart(2, "0")}</div>
 <div className="label-overline text-nanda-muted text-right">{p.season}</div>
 </div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink">{p.name}</h3>
 <div className="text-nanda-muted italic text-xs mt-1">{p.latin}</div>
 <div className="rule-gold w-12 my-5" />
 <div className="label-overline text-nanda-inkSoft mb-4">{p.origin}</div>
 <p className="text-nanda-inkSoft text-[14px] leading-relaxed mb-4"><span className="font-medium text-nanda-ink">« </span>{p.savoirFaire}<span className="font-medium text-nanda-ink"> »</span></p>
 <p className="text-nanda-inkSoft text-[14px] leading-relaxed italic mt-auto pt-4 border-t border-nanda-line">{p.story}</p>
 </div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* CALENDRIER DES RÉCOLTES. NANDA SÁ */}
 {editorial?.harvestCalendar && (
 <section className="border-t border-nanda-line" data-testid={`brand-calendar-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.harvestCalendar.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.harvestCalendar.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.harvestCalendar.intro}</p>
 </FadeUp>
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {editorial.harvestCalendar.months.map((mo, i) => (
 <div key={i} className="bg-nanda-bg p-6 md:p-8" data-testid={`month-${i}`}>
 <div className="label-overline text-nanda-terracotta num-editorial mb-3">{String(i + 1).padStart(2, "0")}</div>
 <div className="font-serif text-xl md:text-2xl text-nanda-ink mb-5">{mo.m}</div>
 <ul className="space-y-2">
 {mo.items.map((it, j) => (
 <li key={j} className="text-nanda-inkSoft text-[14px] leading-snug">· {it}</li>
 ))}
 </ul>
 </div>
 ))}
 </div>
 <p className="mt-10 text-nanda-muted text-sm italic max-w-2xl">{editorial.harvestCalendar.note}</p>
 </div>
 </section>
 )}

 {/* PORTRAITS DE PRODUCTEURS. NANDA SÁ */}
 {editorial?.signatureProducers && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid={`brand-producers-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.signatureProducers.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.signatureProducers.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.signatureProducers.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
 {editorial.signatureProducers.items.map((p, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12 border border-nanda-line" data-testid={`producer-${i}`}>
 <div className="label-overline text-nanda-terracotta mb-6 num-editorial">0{i + 1}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-4">{p.name}</h3>
 <div className="label-overline text-nanda-muted mb-2">{p.craft}</div>
 <div className="text-sm text-nanda-inkSoft italic mb-8">{p.region}</div>
 <div className="rule-gold w-16 mb-8" />
 <p className="font-serif text-lg md:text-xl leading-snug text-nanda-ink italic">{p.quote}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* RECETTES. NANDA SÁ */}
 {editorial?.recipes && (
 <section className="border-t border-nanda-line" data-testid={`brand-recipes-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.recipes.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.recipes.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.recipes.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {editorial.recipes.items.map((r, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12" data-testid={`recipe-${i}`}>
 <div className="label-overline text-nanda-terracotta mb-5 num-editorial">0{i + 1}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-6">{r.title}</h3>
 <div className="label-overline text-nanda-muted mb-6">{r.pairing}</div>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed">{r.body}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* EXPÉRIENCE. JOHN TĀDO (4 univers) */}
 {editorial?.experience && (
 <section className="border-t border-nanda-line" data-testid={`brand-experience-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.experience.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.experience.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.experience.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {editorial.experience.items.map((ex, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-14" data-testid={`experience-${i}`}>
 <div className="flex items-baseline justify-between mb-6">
 <div className="label-overline text-nanda-terracotta">{ex.label}</div>
 <div className="label-overline text-nanda-muted num-editorial">0{i + 1}</div>
 </div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink mb-6">{ex.title}</h3>
 <div className="rule-gold w-24 mb-8" />
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{ex.body}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* CALENDRIER D'OUVERTURE. JOHN TĀDO */}
 {editorial?.milestones && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid={`brand-milestones-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.milestones.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{editorial.milestones.title}</h2>
 </FadeUp>
 <Stagger className="space-y-0 divide-y divide-nanda-line border-y border-nanda-line">
 {editorial.milestones.steps.map((s, i) => (
 <StaggerItem key={i} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-12" data-testid={`milestone-${i}`}>
 <div className="md:col-span-2">
 <div className="font-serif text-4xl md:text-5xl text-nanda-terracotta num-editorial">{s.phase}</div>
 </div>
 <div className="md:col-span-4">
 <div className="label-overline text-nanda-muted mb-2">{s.label}</div>
 </div>
 <div className="md:col-span-6 text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{s.body}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* PRODUITS & SOLUTIONS. ENERGIA PRO */}
 {brand.slug === "energia-pro" && (
 <section id="offre" className="border-t border-nanda-line bg-nanda-bg" data-testid="energia-offer-section">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{l("Notre offre", "Our offer", "A nossa oferta")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
 {l("Nos produits et solutions.", "Our products and solutions.", "Os nossos produtos e soluções.")}
 </h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed mb-4">
 {l(
 "NANDA SÁ ENERGIA PRO ne se limite pas aux solutions solaires. Nous proposons également une gamme d'équipements techniques destinés aux professionnels de l'énergie, du bâtiment, de l'industrie et des travaux publics.",
 "NANDA SÁ ENERGIA PRO is not limited to solar solutions. We also offer a range of technical equipment for professionals in energy, construction, industry and public works.",
 "A NANDA SÁ ENERGIA PRO não se limita às soluções solares. Propomos igualmente uma gama de equipamentos técnicos destinados aos profissionais da energia, da construção, da indústria e das obras públicas."
 )}
 </p>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {l(
 "Nous sélectionnons des équipements de qualité professionnelle auprès de fabricants et de marques reconnus à l'international afin de garantir la fiabilité, la sécurité et la performance de nos solutions.",
 "We source professional-grade equipment from internationally recognised manufacturers and brands to guarantee reliability, safety and performance.",
 "Seleccionamos equipamentos de qualidade profissional junto de fabricantes e marcas reconhecidos internacionalmente para garantir a fiabilidade, a segurança e o desempenho das nossas soluções."
 )}
 </p>
 </FadeUp>

 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
 {[
 {
 icon: Sun,
 overline: "01 · Solutions solaires",
 title: l("Solutions solaires", "Solar solutions", "Soluções solares"),
 items: [
 l("Panneaux photovoltaïques", "Photovoltaic panels", "Painéis fotovoltaicos"),
 l("Batteries lithium", "Lithium batteries", "Baterias de lítio"),
 l("Onduleurs hybrides", "Hybrid inverters", "Inversores híbridos"),
 l("Systèmes hybrides", "Hybrid systems", "Sistemas híbridos"),
 l("Systèmes de stockage d'énergie", "Energy storage systems", "Sistemas de armazenamento de energia"),
 ],
 },
 {
 icon: Zap,
 overline: "02 · Matériel électrique",
 title: l("Matériel électrique", "Electrical equipment", "Material eléctrico"),
 items: [
 l("Connecteurs solaires · Connecteurs MC4", "Solar connectors · MC4 connectors", "Conectores solares · Conectores MC4"),
 l("Protections électriques · Disjoncteurs", "Electrical protection · Circuit breakers", "Protecções eléctricas · Disjuntores"),
 l("Coffrets électriques", "Electrical cabinets", "Quadros eléctricos"),
 l("Câbles", "Cables", "Cabos"),
 l("Structures de fixation & accessoires d'installation", "Mounting structures & installation accessories", "Estruturas de fixação e acessórios de instalação"),
 ],
 },
 {
 icon: Wrench,
 overline: "03 · Outillage photovoltaïque",
 title: l("Outillage photovoltaïque", "Photovoltaic tooling", "Ferramentas fotovoltaicas"),
 items: [
 l("Pinces à sertir", "Crimping pliers", "Alicates de cravar"),
 l("Coupe-câbles", "Cable cutters", "Corta-cabos"),
 l("Pinces à dénuder", "Wire strippers", "Descarnadores de fios"),
 l("Outils d'installation photovoltaïque", "PV installation tools", "Ferramentas de instalação fotovoltaica"),
 ],
 },
 {
 icon: Flame,
 overline: "04 · Soudure",
 title: l("Matériel & consommables de soudure", "Welding equipment & consumables", "Material e consumíveis de soldadura"),
 items: [
 l("Électrodes de soudure", "Welding electrodes", "Eléctrodos de soldadura"),
 l("Meules de découpe", "Cutting discs", "Discos de corte"),
 l("Meules d'ébarbage", "Grinding discs", "Discos de rebarbar"),
 l("Consommables de soudure", "Welding consumables", "Consumíveis de soldadura"),
 ],
 },
 {
 icon: HardHat,
 overline: "05 · EPI",
 title: l("Équipements de Protection Individuelle", "Personal Protective Equipment", "Equipamentos de Protecção Individual"),
 items: [
 l("Gants de protection · Bottes de sécurité", "Protective gloves · Safety boots", "Luvas de protecção · Botas de segurança"),
 l("Chaussures de sécurité · Lunettes de sécurité", "Safety shoes · Safety glasses", "Calçado de segurança · Óculos de segurança"),
 l("Casques · Gilets haute visibilité", "Helmets · High-visibility vests", "Capacetes · Coletes de alta visibilidade"),
 l("Harnais · Vêtements de travail", "Harnesses · Workwear", "Arneses · Vestuário de trabalho"),
 ],
 },
 {
 icon: Hammer,
 overline: "06 · Quincaillerie technique",
 title: l("Quincaillerie technique", "Technical hardware", "Ferragens técnicas"),
 items: [
 l("Visserie", "Screws", "Parafusaria"),
 l("Fixations", "Fastenings", "Fixações"),
 l("Outillage", "Tools", "Ferramentas"),
 l("Accessoires techniques", "Technical accessories", "Acessórios técnicos"),
 ],
 },
 ].map((cat, idx) => {
 const Icon = cat.icon;
 return (
 <StaggerItem
 key={idx}
 className="group bg-white rounded-2xl border border-nanda-line/70 shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_36px_-10px_rgba(0,0,0,0.14)] hover:-translate-y-1 transition-all duration-500 p-8 md:p-9 flex flex-col"
 data-testid={`energia-category-${idx}`}
 >
 <div className="flex items-start justify-between mb-6">
 <div className="text-nanda-terracotta group-hover:scale-110 transition-transform duration-500">
 <Icon size={34} strokeWidth={1.3} />
 </div>
 <div className="label-overline text-nanda-muted text-[10px] text-right max-w-[55%]">{cat.overline}</div>
 </div>
 <h3 className="font-serif text-xl md:text-2xl leading-tight text-nanda-ink mb-5">{cat.title}</h3>
 <ul className="space-y-2.5 flex-1">
 {cat.items.map((it, i) => (
 <li key={i} className="flex gap-3 text-nanda-inkSoft text-[14px] leading-relaxed">
 <Check size={14} strokeWidth={1.8} className="text-nanda-gold flex-shrink-0 mt-1" />
 <span>{it}</span>
 </li>
 ))}
 </ul>
 </StaggerItem>
 );
 })}
 </Stagger>

 {/* Nos services, bandeau distinct */}
 <FadeUp delay={0.15} className="mt-14 md:mt-16 border-t border-nanda-line pt-14">
 <div className="flex items-center gap-4 mb-6">
 <Wrench size={22} strokeWidth={1.4} className="text-nanda-terracotta" />
 <div className="label-overline text-nanda-muted">{l("Nos services", "Our services", "Os nossos serviços")}</div>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
 {[
 l("Étude et dimensionnement de projets", "Project study and sizing", "Estudo e dimensionamento de projectos"),
 l("Vente de matériel", "Equipment sales", "Venda de material"),
 l("Installation", "Installation", "Instalação"),
 l("Maintenance", "Maintenance", "Manutenção"),
 l("Assistance technique", "Technical support", "Assistência técnica"),
 l("Accompagnement de projets", "Project mentoring", "Acompanhamento de projectos"),
 ].map((s, i) => (
 <div key={i} className="flex gap-3 text-nanda-ink text-[15px] md:text-base">
 <Check size={16} strokeWidth={1.8} className="text-nanda-gold flex-shrink-0 mt-1" />
 <span>{s}</span>
 </div>
 ))}
 </div>
 </FadeUp>

 <FadeUp delay={0.2} className="mt-14 flex flex-wrap gap-3 items-center">
 <div className="label-overline text-nanda-muted mr-2">{l("Clientèle", "Clientele", "Clientela")} :</div>
 {[
 l("Particuliers", "Individuals", "Particulares"),
 l("Entreprises", "Businesses", "Empresas"),
 l("Collectivités", "Local authorities", "Autarquias"),
 l("ONG", "NGOs", "ONG"),
 l("Bâtiment · Industrie · TP", "Construction · Industry · Public works", "Construção · Indústria · Obras públicas"),
 ].map((t, i) => (
 <span key={i} className="label-overline px-4 py-2 border border-nanda-line rounded-full text-nanda-ink bg-white">{t}</span>
 ))}
 </FadeUp>

 <FadeUp delay={0.3} className="mt-14">
 <Link
 to="/devis?brand=energia-pro"
 data-testid="energia-devis-cta"
 className="inline-flex items-center gap-4 bg-nanda-terracotta text-white px-8 py-5 hover:bg-nanda-ink transition-colors duration-500 label-overline text-base"
 >
 {l("Demander un devis", "Request a quote", "Pedir um orçamento")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 )}

 {/* ENGAGEMENT QUALITÉ. ENERGIA PRO */}
 {brand.slug === "energia-pro" && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid="energia-quality-section">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-gold mb-4">{l("Qualité", "Quality", "Qualidade")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-nanda-ink">
 {l("Notre engagement qualité.", "Our quality commitment.", "O nosso compromisso de qualidade.")}
 </h2>
 <div className="rule-gold w-24 mt-8" />
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 <p>
 {l(
 "Chez NANDA SÁ ENERGIA PRO, nous sélectionnons exclusivement des équipements répondant à des standards élevés de qualité, de fiabilité et de performance. Nous privilégions des fabricants et des marques reconnus à l'international afin de garantir des solutions durables, sécurisées et adaptées aux exigences des particuliers, des entreprises, des collectivités et des professionnels.",
 "At NANDA SÁ ENERGIA PRO, we select only equipment that meets high standards of quality, reliability and performance. We favour internationally recognised manufacturers and brands to guarantee durable, safe solutions adapted to the requirements of individuals, businesses, local authorities and professionals.",
 "Na NANDA SÁ ENERGIA PRO, seleccionamos exclusivamente equipamentos que respondem a padrões elevados de qualidade, fiabilidade e desempenho. Privilegiamos fabricantes e marcas reconhecidos internacionalmente para garantir soluções duráveis, seguras e adaptadas às exigências dos particulares, das empresas, das autarquias e dos profissionais."
 )}
 </p>
 <p className="text-nanda-inkSoft">
 {l(
 "Cette exigence de qualité s'applique à l'ensemble de notre offre, des équipements solaires aux accessoires techniques et aux Équipements de Protection Individuelle (EPI).",
 "This quality requirement applies to our entire offer, from solar equipment to technical accessories and Personal Protective Equipment (PPE).",
 "Esta exigência de qualidade aplica-se a toda a nossa oferta, dos equipamentos solares aos acessórios técnicos e aos Equipamentos de Protecção Individual (EPI)."
 )}
 </p>
 <div className="mt-8 flex flex-wrap gap-3">
 {[
 l("Fiabilité", "Reliability", "Fiabilidade"),
 l("Performance", "Performance", "Desempenho"),
 l("Durabilité", "Durability", "Durabilidade"),
 l("Sécurité", "Safety", "Segurança"),
 l("Standards internationaux", "International standards", "Padrões internacionais"),
 ].map((t, i) => (
 <span key={i} className="label-overline px-4 py-2 border border-nanda-line rounded-full text-nanda-inkSoft">{t}</span>
 ))}
 </div>
 </FadeUp>
 </div>
 </section>
 )}

 {/* SECTEURS DESSERVIS. ENERGIA PRO */}
 {editorial?.sectorsServed && (
 <section className="border-t border-nanda-line" data-testid={`brand-sectors-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.sectorsServed.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.sectorsServed.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.sectorsServed.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {editorial.sectorsServed.items.map((s, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-8 md:p-10 group hover:bg-nanda-bgAlt transition-colors duration-500" data-testid={`sector-served-${i}`}>
 <div className="label-overline text-nanda-terracotta mb-4 num-editorial">0{i + 1}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-4">{s.label}</h3>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed">{s.body}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* ÉTUDES DE CAS. ENERGIA PRO */}
 {editorial?.caseStudies && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid={`brand-cases-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.caseStudies.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.caseStudies.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.caseStudies.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
 {editorial.caseStudies.items.map((c, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12 border border-nanda-line flex flex-col" data-testid={`case-${i}`}>
 <div className="label-overline text-nanda-terracotta mb-6 num-editorial">0{i + 1}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-3">{c.client}</h3>
 <div className="label-overline text-nanda-muted mb-2">{c.type}</div>
 <div className="text-sm text-nanda-inkSoft italic mb-8">{c.location}</div>
 <div className="rule-gold w-16 mb-6" />
 <div className="label-overline text-nanda-muted mb-2">{l("Défi", "Challenge", "Desafio")}</div>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed mb-6">{c.challenge}</p>
 <div className="label-overline text-nanda-muted mb-2">{l("Résultat", "Result", "Resultado")}</div>
 <p className="font-serif text-lg text-nanda-ink leading-snug">{c.result}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* FAQ. ENERGIA PRO */}
 {editorial?.faq && (
 <section className="border-t border-nanda-line" data-testid={`brand-faq-${brand.slug}`}>
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20">
 <div className="label-overline text-nanda-muted mb-4">{editorial.faq.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{editorial.faq.title}</h2>
 </FadeUp>
 <div className="divide-y divide-nanda-line border-y border-nanda-line">
 {editorial.faq.items.map((f, i) => (
 <details key={i} className="group py-8 md:py-10" data-testid={`faq-${i}`}>
 <summary className="cursor-pointer flex items-baseline justify-between gap-8 list-none">
 <div className="flex items-baseline gap-6">
 <span className="label-overline text-nanda-terracotta num-editorial shrink-0">0{i + 1}</span>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500">{f.q}</h3>
 </div>
 <span className="label-overline text-nanda-muted group-open:rotate-45 transition-transform duration-500 text-2xl leading-none shrink-0">+</span>
 </summary>
 <div className="mt-6 md:pl-16 text-nanda-inkSoft text-[15px] md:text-base leading-relaxed max-w-3xl">
 {f.a}
 </div>
 </details>
 ))}
 </div>
 </div>
 </section>
 )}

 {/* PROGRAMMES. Académie */}
 {editorial?.programs && (
 <section className="border-t border-nanda-line" data-testid={`brand-programs-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.programs.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">{editorial.programs.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.programs.intro}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {editorial.programs.items.map((p, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12 flex flex-col" data-testid={`program-${i}`}>
 <div className="label-overline text-nanda-terracotta mb-5">{p.format}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-4">{p.title}</h3>
 <div className="rule-gold w-16 my-6" />
 <div className="label-overline text-nanda-muted mb-3">{l("Public", "Audience", "Público")}</div>
 <div className="text-nanda-inkSoft text-sm mb-6 italic">{p.audience}</div>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed mt-auto">{p.body}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* PUBLICS. Académie */}
 {editorial?.audiences && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid={`brand-audiences-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.audiences.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{editorial.audiences.title}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {editorial.audiences.items.map((a, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-8 md:p-10" data-testid={`audience-${i}`}>
 <div className="label-overline text-nanda-terracotta mb-4 num-editorial">0{i + 1}</div>
 <h3 className="font-serif text-xl md:text-2xl leading-tight text-nanda-ink mb-4">{a.label}</h3>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed">{a.body}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* IMPACT SOCIAL. Académie */}
 {editorial?.impact && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid={`brand-impact-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{editorial.impact.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-nanda-ink mb-8">{editorial.impact.title}</h2>
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">{editorial.impact.body}</p>
 </FadeUp>
 <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-14">
 {editorial.impact.metrics.map((m, i) => (
 <StaggerItem key={i} data-testid={`impact-metric-${i}`}>
 <div className="font-serif text-6xl md:text-7xl lg:text-8xl text-nanda-ink num-editorial leading-none">{m.value}</div>
 <div className="label-overline text-nanda-inkSoft mt-5">{m.label}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* PARTENARIATS. Académie */}
 {editorial?.partnerships && (
 <section className="border-t border-nanda-line" data-testid={`brand-partnerships-${brand.slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-muted mb-4">{editorial.partnerships.overline}</div>
 <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">{editorial.partnerships.title}</h2>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-7">
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed mb-10">{editorial.partnerships.body}</p>
 <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4">
 {editorial.partnerships.categories.map((c, i) => (
 <li key={i} className="flex items-baseline gap-4 font-serif text-xl md:text-2xl text-nanda-ink">
 <span className="h-px w-8 bg-nanda-ink" />{c}
 </li>
 ))}
 </ul>
 </FadeUp>
 </div>
 </section>
 )}

 {/* ENGAGEMENTS */}
 {editorial?.engagements && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-muted mb-4">{l("Engagements", "Commitments", "Compromissos")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-nanda-ink leading-[1.05]">{l("Nos engagements", "Our commitments", "Os nossos compromissos")}</h2>
 </FadeUp>
 <Stagger className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
 {editorial.engagements.map((e, i) => (
 <StaggerItem key={i} className="flex items-start gap-4">
 <Check size={18} strokeWidth={1.5} className="mt-1 text-nanda-inkSoft" />
 <span className="text-nanda-ink text-base md:text-[17px] leading-snug">{e}</span>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* PRODUCERS */}
 {editorial?.producers && (
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-5">{l("Producteurs", "Producers", "Produtores")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-12">{editorial.producers.title}</h2>
 <div className="max-w-2xl space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {editorial.producers.body.map((p, i) => <p key={i}>{p}</p>)}
 </div>
 </FadeUp>
 </div>
 </section>
 )}

 {/* WHY + SIGNATURE */}
 {editorial?.why && (
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-5">{l("Pourquoi", "Why", "Porquê")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-12">{editorial.why.title}</h2>
 <div className="max-w-2xl space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {editorial.why.body.map((p, i) => <p key={i}>{p}</p>)}
 </div>
 {editorial.signature && (
 <div className="mt-20 pt-10 border-t border-nanda-line">
 <div className="label-overline text-nanda-muted mb-5">{l("Signature", "Signature", "Assinatura")}</div>
 <p className="font-serif text-2xl md:text-3xl text-nanda-ink leading-snug max-w-2xl">{editorial.signature}</p>
 </div>
 )}
 </FadeUp>
 </div>
 </section>
 )}

 {/* CTA Contact */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-7">
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05] text-nanda-ink">
 {l("Discutons de votre projet.", "Let us discuss your project.", "Falemos do seu projecto.")}
 </h2>
 <p className="mt-6 max-w-xl text-nanda-inkSoft text-base md:text-[17px]">
 {l("Partenariats, distribution, presse, notre équipe vous répondra rapidement.", "Partnerships, distribution, press, our team will get back to you quickly.", "Parcerias, distribuição, imprensa, a nossa equipa responderá rapidamente.")}
 </p>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 flex md:justify-end items-end">
 <Link
 to={`/contact?brand=${brand.slug}`}
 data-testid="brand-cta-contact"
 className="inline-flex items-center gap-3 bg-nanda-terracotta text-white px-7 py-4 hover:bg-[#8F5B3F] transition-colors duration-500 label-overline"
 >
 {l("Nous contacter", "Contact us", "Contactar-nos")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>

 {/* Other brands */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <div className="label-overline text-nanda-muted mb-10">{l("Les autres maisons", "The other houses", "As outras casas")}</div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {otherBrands.map((b) => (
 <Link
 key={b.slug}
 to={`/marques/${b.slug}`}
 data-testid={`other-brand-${b.slug}`}
 className={`group relative block overflow-hidden border border-nanda-line aspect-[4/5] ${b.heroStyle === "product" ? "bg-[#F5EDE2]" : ""}`}
 >
 <img
 src={b.image}
 alt={b.name}
 loading="lazy"
 className={`absolute inset-0 h-full w-full transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${b.heroStyle === "product" ? "object-contain p-6" : "object-cover"}`}
 />
 {b.heroStyle !== "product" && (
 <div className="absolute inset-0 bg-gradient-to-t from-nanda-ink/70 via-nanda-ink/10 to-transparent" />
 )}
 <div className="absolute inset-0 p-7 flex flex-col justify-end">
 <div className={`font-serif text-3xl ${b.heroStyle === "product" ? "text-nanda-ink" : "text-white"}`}>{b.name}</div>
 <div className={`label-overline mt-2 ${b.heroStyle === "product" ? "text-nanda-inkSoft" : "text-white/80"}`}>{b.category}</div>
 </div>
 </Link>
 ))}
 </div>
 </div>
 </section>
 </>
 )}
 </div>
 );
}
